/**
 * Desk official logo — uses brand PNG assets.
 *
 * Both PNGs are square with transparent padding around the actual logo content.
 * We render an oversized image inside an overflow:hidden container so only the
 * visible logo content shows — empty padding gets cropped.
 *
 * Variants:
 *   - "full"   → /logo escura.png  (horizontal: mark + DESK wordmark, dark navy — for light bg)
 *   - "symbol" → /logotipo.png     (mark only)
 *
 * `size` controls the container HEIGHT.
 */
import { useId } from 'react';

export function DeskLogo({ variant = 'full', size = 48, theme = 'light', className, style }) {
    const isSymbol = variant === 'symbol';
    const isDark = theme === 'dark';
    const src = isSymbol ? '/logotipo.png' : (isDark ? '/logo clara.png' : '/logo escura.png');

    if (isSymbol) {
        return (
            <img
                src={src}
                alt="Desk"
                className={className}
                style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    objectFit: 'contain',
                    display: 'block',
                    flexShrink: 0,
                    filter: isDark ? undefined : 'invert(1) brightness(0.2)',
                    transition: 'filter 220ms ease',
                    ...style,
                }}
            />
        );
    }

    // Full variant: content ~4:1 horizontal → frame size×(size*4), square img fitting width
    const containerWidth = size * 4;
    return (
        <div
            className={className}
            style={{
                width: `${containerWidth}px`,
                height: `${size}px`,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                ...style,
            }}
        >
            <img
                src={src}
                alt="Desk"
                style={{
                    width: `${containerWidth}px`,
                    height: `${containerWidth}px`,
                    objectFit: 'contain',
                    flexShrink: 0,
                }}
            />
        </div>
    );
}

/**
 * Stylized SVG mark (animatable). Use when CSS path-draw animation is needed.
 */
export function DeskMark({ size = 56, animated = false, glow = true, className }) {
    // useId em vez de Math.random(): o id precisa ser ESTÁVEL entre renders, porque os
    // url(#...) abaixo apontam para os gradientes/filtro definidos em <defs>. Com random,
    // cada render sorteava um id novo — além de ser função impura no corpo do componente.
    // Os ":" que o useId gera saem fora: são válidos em id de HTML, mas quebram se algum dia
    // alguém tentar alcançar esses nós por seletor CSS/querySelector.
    const id = `desk-${useId().replace(/:/g, "")}`;
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 100 100"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            style={glow ? { filter: 'drop-shadow(0 0 16px rgba(0, 214, 143, 0.32))' } : undefined}
            aria-label="Desk"
        >
            <defs>
                <linearGradient id={`${id}-pen`} x1="50%" y1="0%" x2="50%" y2="100%">
                    <stop offset="0%"  stopColor="#F1F5FF" stopOpacity="0.95" />
                    <stop offset="65%" stopColor="#9DDABF" stopOpacity="0.85" />
                    <stop offset="100%" stopColor="#00D68F" stopOpacity="1" />
                </linearGradient>
                <linearGradient id={`${id}-surface`} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%"   stopColor="#F1F5FF" stopOpacity="0.85" />
                    <stop offset="100%" stopColor="#7AA8C8" stopOpacity="0.55" />
                </linearGradient>
                <radialGradient id={`${id}-origin`} cx="50%" cy="50%" r="50%">
                    <stop offset="0%"   stopColor="#5FFFCB" stopOpacity="1" />
                    <stop offset="40%"  stopColor="#00D68F" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#00D68F" stopOpacity="0" />
                </radialGradient>
                <filter id={`${id}-blur`} x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="2.5" />
                </filter>
            </defs>

            <path d="M14 72 L86 72 L72 80 L28 80 Z" fill={`url(#${id}-surface)`} className={animated ? 'line-path' : undefined} />
            <path d="M55 18 L62 18 L59 72 L48 72 Z" fill={`url(#${id}-pen)`} className={animated ? 'line-path' : undefined} />
            <ellipse cx="55" cy="74" rx="14" ry="5" fill={`url(#${id}-origin)`} filter={`url(#${id}-blur)`} />
            <circle cx="55" cy="74" r="2.6" fill="#5FFFCB" />
        </svg>
    );
}
